// AiBubbleStack — THE WORDS ABOVE A BAR, AS BUBBLES (Nika 2026-10-28: "in
// minimized mode, captions should be like bubble and should not resize ai
// navbar" / "when AI asks for confirmation, in minimized mode its not
// visible").
//
// A bar — the minimized assistant island (SimoneReasoningPill) or a composer
// that hosts a voice call (VoiceStrip) — is ONE row at the control height.
// What was said floats ABOVE it in this absolutely positioned column, so the
// bar never grows: the same height with no turn, one turn or ten, and while a
// reply streams. The column takes no layout space and no pointer of its own;
// only the bubbles do.
//
//   • BubbleCaptions — the newest two lines (each an AiCaptionBubble, below),
//     oldest first, so the newest sits nearest the bar: the person's line on
//     the right (one line), the
//     assistant's on the left (three lines; while it streams, its newest
//     words). The line before them fades out as a new one arrives. A press
//     opens the conversation: the thread for a text exchange, the call screen
//     for a call.
//   • AiAsk — what waits on the person while the thread is not on screen: a
//     step for the OK (the step, what it will write, Run · Skip · Run all on
//     the row's own settle) or a question for a pick (its prompt, one button
//     per option on the card's own writer), and a polite live region that
//     names it (_simone/AiAsk.js — the ONE ask; the full-screen call mounts it
//     too). It sits nearest the bar, so it never moves while the captions
//     above it change.
//
// Rows: a host may pass `turnsSource` (the island hands in its thread rows in
// the text posture — the VoiceLog contract); without it the call's own turns
// are read: `voiceTurns`, then the live partial as the person's streaming line.
//
// Every factory is self-contained and every helper lives in `scope` (FA204 —
// frank serializes each factory on its own).
export const AiBubbleStack = {
  scope: {
    // The call's lines: the engine-agnostic turns (an `action` kind is a
    // command result), then what the engine hears right now.
    voiceRows(root) {
      const r = root || {}
      const turns = Array.isArray(r.voiceTurns) ? r.voiceTurns : []
      const out = []
      for (let i = 0; i < turns.length; i++) {
        const t = turns[i]
        const text = t && typeof t.text === 'string' ? t.text.trim() : ''
        if (!text) continue
        out.push({
          id: String(t.id || 'vt' + i),
          role: t.kind === 'action' ? 'action' : t.role === 'user' ? 'user' : 'agent',
          text,
          streaming: !!t.streaming
        })
      }
      const heard = typeof r.voiceInterim === 'string' ? r.voiceInterim.trim() : ''
      if (heard) out.push({ id: 'voice-interim', role: 'user', text: heard, streaming: true })
      return out
    },

    // The bubbles on show: the newest two lines plus the one before them,
    // marked `older` — it fades out where it stands instead of vanishing when
    // a new line pushes it off. `stack` is the AiBubbleStack element (it
    // carries the host's `turnsSource`).
    captions(stack, s) {
      const src = stack && stack.turnsSource
      const own = typeof src === 'function' ? src(stack, s) : src
      const thread = Array.isArray(own)
      const rows = thread ? own : this.voiceRows(s && s.root)
      const tail = rows.slice(-3)
      return tail.map((row, i) => ({
        key: 'cap-' + String(row.id || i),
        id: String(row.id || i),
        role: row.role === 'user' ? 'user' : row.role === 'action' ? 'action' : 'agent',
        text: String(row.text || ''),
        streaming: !!row.streaming,
        source: thread ? 'thread' : 'voice',
        older: tail.length === 3 && i === 0
      }))
    }
  },

  // Above the bar's top edge, as wide as the bar, never in its flow. Above the
  // island's full-surface hit area and its trailing buttons (z 1 and 2).
  position: 'absolute',
  left: '0',
  right: '0',
  bottom: '100%',
  zIndex: '3',
  flow: 'y',
  align: 'stretch flex-end',
  gap: 'Y',
  paddingBottom: 'Z',
  pointerEvents: 'none',
  textAlign: 'left',

  BubbleCaptions: {
    flow: 'y',
    align: 'flex-start flex-end',
    gap: 'Y',
    minWidth: '0',
    childrenAs: 'state',
    children: (el, s) => el.scope.captions(el.parent, s),
    childExtends: 'AiCaptionBubble'
  },

  // The ask, nearest the bar: the shared component (its card and its live
  // region sit in this column's flow — its wrapper is `display: contents`).
  AiAsk: {}
}

// AiCaptionBubble — ONE line of the conversation above a bar (AiBubbleStack
// renders the newest two, its rows as this bubble's state: { role, text,
// streaming, source, older }). An opaque sheet (the dialog ground), the
// person's line on the right and quieter, the assistant's on the left. A
// button: a press opens the conversation the line belongs to.
export const AiCaptionBubble = {
  scope: {
    // One bubble's words: whitespace folded and the loudest markdown marks
    // dropped (a caption is a line, not a document). The assistant's line
    // carries the ellipsis while it streams; the person's live partial keeps
    // its newest words.
    captionText(row) {
      const text = String((row && row.text) || '')
        .replace(/\*\*|__|`/g, '')
        .replace(/(^|\s)#{1,6}\s/g, '$1')
        .replace(/\s+/g, ' ')
        .trim()
      if (!row || !row.streaming) return text
      if (row.role === 'user') return text.length > 64 ? '…' + text.slice(-63) : text
      return text + '…'
    },

    // A press on a line opens the conversation it belongs to. A call → the
    // call screen (voiceExpand). The thread → the drawer, exactly as a press
    // on the bar itself does: a minimized dock moves to the right edge for
    // the session (never the saved placement — "Open in main" is the head's
    // own explicit move), the slot handle retires, and a landed answer is
    // read. Every shell move is guarded (the standalone canvas has none).
    openCaption(el, s) {
      const root = s && s.root
      if (!root || typeof root.update !== 'function') return
      const fns = (el.context && el.context.functions) || {}
      if (s.source === 'voice') {
        if (typeof fns.voiceExpand === 'function') el.call('voiceExpand')
        return
      }
      const patch = { assistantOpen: true }
      if (root._assistantPillHandle) patch._assistantPillHandle = false
      if (root.assistantDock === 'minimized') patch.assistantDock = 'right'
      root.update(patch)
      if (typeof fns.assistantAnswerPopoverDismiss === 'function') {
        el.call('assistantAnswerPopoverDismiss')
      }
    }
  },

  tag: 'button',
  type: 'button',
  display: 'block',
  minWidth: '0',
  maxWidth: (el, s) => (s.role === 'user' ? '80%' : '100%'),
  alignSelf: (el, s) => (s.role === 'user' ? 'flex-end' : 'flex-start'),
  padding: 'Y Z1',
  theme: 'dialog',
  shadow: 'float',
  round: 'radiusCard',
  borderWidth: '0',
  textAlign: 'left',
  fontSize: 'Z1',
  lineHeight: '1.45',
  color: (el, s) => (s.role === 'user' ? 'paragraph' : 'title'),
  cursor: 'pointer',
  pointerEvents: 'auto',
  // The house enter for a new line; the line that is pushed off fades where
  // it stands (opacity, never a jump) and leaves the tab order. A line that
  // mounts already pushed off never enters at all.
  animationName: 'fadeInUpShort',
  animationDuration: 'B',
  transition: 'B opacity defaultBezier, A background-color defaultBezier',
  // A fill step that stays OPAQUE over the page: the ink mixed into the
  // sheet's own ground (`tintBase` is the dialog fill in both schemes).
  ':hover': { background: 'color-mix(in srgb, currentColor 6%, var(--color-tintBase))' },
  ':active': { background: 'color-mix(in srgb, currentColor 10%, var(--color-tintBase))' },
  isOlder: (el, s) => !!s.older,
  '.isOlder': { opacity: '0', pointerEvents: 'none', animationName: 'none' },
  tabindex: (el, s) => (s.older ? '-1' : '0'),
  aria: { hidden: (el, s) => (s.older ? 'true' : null) },
  // The overflow law: a clamped line carries its whole text in the title.
  title: (el, s) => el.scope.captionText({ text: s.text }),
  onClick: (e, el, s) => {
    e.stopPropagation()
    el.scope.openCaption(el, s)
  },

  BubbleWords: {
    tag: 'span',
    display: 'block',
    overflowWrap: 'anywhere',
    lineClamp: (el, s) => (s.role === 'user' ? 1 : 3),
    text: (el, s) => el.scope.captionText(s),
    // While the assistant streams, the box shows its NEWEST three lines: a
    // bottom-aligned column clips its overflow at the top.
    isTail: (el, s) => !!s.streaming && s.role !== 'user',
    '.isTail': {
      lineClamp: 'none',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'flex-end',
      maxHeight: '3lh',
      overflow: 'hidden'
    }
  }
}
